import User from 'types/user';
import { FC, useEffect, useState } from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import roleList from 'types/roles';
import Utilities from 'utils/utilities';
import SelectOption from 'types/selectOption';
import useAuth from 'hooks/useAuth';
import temperatureConfList from 'types/temperatureConf';

interface ChildComponentProps {
    user?: User;
    onAdd(user: User): any;
    onClose(): any;
}

const UserAdd: FC<ChildComponentProps> = (props) => {
    const { user, onAdd, onClose } = props;
    const [open, setOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState(user);
    const { user: loggedUser } = useAuth();
    const { t } = useTranslation();
    const theme = useTheme();
    const roles: SelectOption[] = roleList(t);
    const mobileDevice = useMediaQuery(theme.breakpoints.down('md'));
    const temperatureConf: SelectOption[] = temperatureConfList();
    const utilities = new Utilities();

    useEffect(() => {
        if (user === undefined) setOpen(false);
        else {
            const userCopy = {
                ...user!,
                role: 'User'
            };
            setCurrentUser(userCopy);
            setOpen(true);
        }
    }, [user]);

    const handleRole = (e: any) => {
        const { value } = utilities.getFormValue(e);
        let userCopy = null;

        if (value === 'System_Admin') {
            userCopy = {
                ...currentUser!,
                isSystemAdmin: true,
                role: 'System_Admin',
                otpEnabled: true
            };
        } else {
            userCopy = {
                ...currentUser!,
                role: value,
                isSystemAdmin: false,
                otpEnabled: false
            };
        }
        setCurrentUser(userCopy);
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title"> {t('ADD_NEW_USER')} </DialogTitle>
                <DialogContent sx={{ paddingTop: '6px!important', width: mobileDevice ? window.innerWidth - 65 : 500 }}>
                    <Grid container>
                        <Grid item xs={12} sx={{ mb: 2, mt: 1 }}>
                            <FormControl fullWidth>
                                <InputLabel> {t('NAME')}</InputLabel>
                                <OutlinedInput
                                    defaultValue={user?.name}
                                    onChange={(d) => {
                                        const e = {
                                            ...currentUser!,
                                            name: d.target.value
                                        };
                                        setCurrentUser(e);
                                    }}
                                    type="text"
                                    label={t('NAME')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sx={{ mb: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel> {t('SURNAME')}</InputLabel>
                                <OutlinedInput
                                    defaultValue={user?.surname}
                                    onChange={(d) => {
                                        const e = {
                                            ...currentUser!,
                                            surname: d.target.value
                                        };
                                        setCurrentUser(e);
                                    }}
                                    type="text"
                                    label={t('SURNAME')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sx={{ mb: 2 }}>
                            <FormControl fullWidth required>
                                <InputLabel> {t('Email')}</InputLabel>
                                <OutlinedInput
                                    defaultValue={user?.username}
                                    onChange={(d) => {
                                        const e = {
                                            ...currentUser!,
                                            username: d.target.value
                                        };
                                        setCurrentUser(e);
                                    }}
                                    type="text"
                                    label={t('Email')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sx={{ mb: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel> {t('COMPANY')}</InputLabel>
                                <OutlinedInput
                                    defaultValue={user?.company}
                                    onChange={(d) => {
                                        const e = {
                                            ...currentUser!,
                                            company: d.target.value
                                        };
                                        setCurrentUser(e);
                                    }}
                                    type="text"
                                    label="Company"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sx={{ mb: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel> {t('ROLE')}</InputLabel>
                                <Select
                                    label={t('ROLE')}
                                    name="role"
                                    onChange={handleRole}
                                    sx={{ color: 'white' }}
                                    value={currentUser?.role}
                                    defaultValue="User"
                                >
                                    {roles.map((role: any) => {
                                        if (role.value === 'System_Admin' && !loggedUser?.isSystemAdmin) {
                                            return null;
                                        }
                                        return (
                                            <MenuItem value={role.value} key={role.value}>
                                                {t(role.label)}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item md={6} xs={6} sx={{ mb: 2, textAlign: 'center' }}>
                            <FormControl fullWidth required>
                                <>{t('ENABLE_OTP')}</>
                                <Checkbox
                                    disabled={currentUser?.role === 'System_Admin'}
                                    checked={currentUser?.otpEnabled}
                                    onChange={(d) => {
                                        const e = {
                                            ...currentUser!,
                                            otpEnabled: d.target.checked
                                        };
                                        setCurrentUser(e);
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth required>
                                <InputLabel> {t('TEMPERATURE_UNIT')}</InputLabel>
                                <Select
                                    label={t('TEMPERATURE_UNIT')}
                                    name="temperatureUnit"
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        const updatedUser = {
                                            ...currentUser!,
                                            preferredTemperatureUnit: newValue
                                        };
                                        setCurrentUser(updatedUser);
                                    }}
                                    sx={{ color: 'white' }}
                                    value={currentUser?.preferredTemperatureUnit!}
                                >
                                    {temperatureConf.map((temperature) => (
                                        <MenuItem value={temperature.value} key={temperature.value}>
                                            {t(temperature.label)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Typography marginTop="5px"> * {t('MANDATORY_FIELDS')}</Typography>
                </DialogContent>
                <DialogActions sx={{ marginRight: '16px' }}>
                    <Button
                        onClick={() => {
                            setCurrentUser(undefined);
                            onClose();
                        }}
                        autoFocus
                        style={{
                            border: '2px solid',
                            borderColor: theme.palette.secondary.main,
                            borderRadius: 7
                        }}
                    >
                        <Typography sx={{ color: theme.palette.primary.main }}> {t('CANCEL')}</Typography>
                    </Button>
                    <Button
                        style={{
                            border: '2px solid',
                            borderColor: theme.palette.primary.main,
                            borderRadius: 7,
                            background: theme.palette.primary.main
                        }}
                        onClick={() => {
                            onAdd(currentUser!);
                        }}
                    >
                        <Typography style={{ color: '#fff' }}>{t('ADD')}</Typography>
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UserAdd;
